import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Encoder-only announcer: periodic weather updates, plus an immediate read-out when a warning cut-in fires.
export function WeatherVoice({ cutIn, slideId, nextSlideId, onSpeaking, block = false }) {
  const audioRef = useRef(null);
  const lastSpokenRef = useRef({ id: null, at: 0 });
  const spokenCutInRef = useRef(null);
  const stationHourRef = useRef(null);
  const outlookAiringRef = useRef(false);
  const radarWarmRef = useRef(0);
  const briefingRef = useRef({});
  const advisoryRef = useRef(new Set());
  const mountedAtRef = useRef(Date.now());
  const introRef = useRef({ slide: null, at: 0, warmed: null });
  const [state, setState] = useState({ clipId: null, speaking: false, kind: null });
  const blocked = !!cutIn || !!block;

  const play = (src, clipId, kind) => {
    const el = audioRef.current;
    if (!el) return;
    el.src = src;
    el.volume = 1;
    el.play().then(() => setState({ clipId, speaking: true, kind })).catch(() => {});
  };

  // Top-of-the-hour station ID (first 2 minutes of the hour, Eastern), then the scheduled update
  const etNow = () => {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "numeric", hour12: false }).formatToParts(new Date());
    const get = (t) => Number(parts.find((p) => p.type === t)?.value);
    return { hour: get("hour") % 24, minute: get("minute") };
  };
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const el = audioRef.current;
        const { hour, minute } = etNow();
        if (el && !blocked && el.paused && minute < 2 && stationHourRef.current !== hour) {
          const { data: st } = await api.get(`/voice/station-id?hour=${hour}`);
          if (cancelled) return;
          stationHourRef.current = hour;
          if (st.url) { play(`${BASE}${st.url}`, `station-${hour}`, "station"); return; }
        }
        const { data } = await api.get("/voice/current");
        if (cancelled || !data.enabled) return;
        // Scheduled TV-style briefing: prefetch 2 min early (TTS takes ~10 s), air within 3 min of the slot, once per day
        const nowMin = hour * 60 + minute;
        const dayKey = new Date().toLocaleDateString("en-US", { timeZone: "America/New_York" });
        for (const slot of data.briefing_times || []) {
          const [sh, sm] = slot.split(":").map(Number);
          const slotMin = sh * 60 + sm;
          const played = briefingRef.current[`${dayKey}|${slot}`];
          if (!played && nowMin >= slotMin - 2 && nowMin < slotMin && !briefingRef.current[`warm|${dayKey}|${slot}`]) {
            briefingRef.current[`warm|${dayKey}|${slot}`] = true;
            api.get(`/voice/briefing?slot=${slot}`).catch(() => {});
          }
          if (!played && nowMin >= slotMin && nowMin < slotMin + 3 && el && !blocked && el.paused) {
            const { data: b } = await api.get(`/voice/briefing?slot=${slot}`);
            if (cancelled) return;
            briefingRef.current[`${dayKey}|${slot}`] = true;
            if (b.url) { play(`${BASE}${b.url}`, `briefing-${slot}`, "briefing"); return; }
          }
        }
        // Calm watch/advisory announcements issued since this page loaded, each read once
        if (data.advisory_voice && el && !blocked && el.paused) {
          const { data: adv } = await api.get("/voice/advisories?minutes=30");
          if (cancelled) return;
          const next = (adv.items || []).find((a) => !advisoryRef.current.has(a.id) && new Date(a.created_at).getTime() > mountedAtRef.current - 120000);
          if (next) { advisoryRef.current.add(next.id); play(`${BASE}${next.url}`, next.id, "advisory"); return; }
        }
        if (!data.url) return;
        // Keep the live-radar callout clip warm so it's ready the moment the radar slide airs
        if (Date.now() - radarWarmRef.current > 4 * 60 * 1000) { radarWarmRef.current = Date.now(); api.get("/voice/radar").catch(() => {}); }
        const { id, at } = lastSpokenRef.current;
        const due = Date.now() - at >= (data.interval_min * 60 * 1000) - 5000;
        if (el && !blocked && data.clip_id !== id && due && el.paused) {
          lastSpokenRef.current = { id: data.clip_id, at: Date.now() };
          play(`${BASE}${data.url}`, data.clip_id, "update");
        }
      } catch { /* keep looping */ }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [blocked]);

  // Cut-in: stop any regular update at once, then read the warning as soon as its clip is ready
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!cutIn) { spokenCutInRef.current = null; return; }
    if (state.kind !== "cutin" && !el.paused) { el.pause(); setState((s) => ({ ...s, speaking: false })); }
    if (cutIn.voice_url && spokenCutInRef.current !== cutIn.id) {
      spokenCutInRef.current = cutIn.id;
      play(`${BASE}${cutIn.voice_url}`, cutIn.id, "cutin");
    }
  }, [cutIn, state.kind]);

  // Slide-triggered read-outs (SPC outlook risk, live-radar county callout): once per airing, skipped if anything else is mid-sentence
  const SLIDE_VOICE = { "ga-outlook": "/voice/outlook", "hrrr-reflectivity": "/voice/radar" };
  useEffect(() => {
    const endpoint = SLIDE_VOICE[slideId];
    if (!endpoint) { outlookAiringRef.current = false; return undefined; }
    if (outlookAiringRef.current === slideId || blocked) return undefined;
    outlookAiringRef.current = slideId;
    let cancelled = false;
    api.get(endpoint).then(({ data }) => {
      const el = audioRef.current;
      if (cancelled || !data.enabled || !data.url || !el || !el.paused) return;
      play(`${BASE}${data.url}`, data.url.split("/").pop().replace(".mp3", ""), slideId === "ga-outlook" ? "outlook" : "radar");
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId, blocked]);

  // Slide intros ("Here's your Futurecast…"): one per airing, never over another clip, and at most one every gap_s seconds.
  useEffect(() => {
    if (!slideId || SLIDE_VOICE[slideId] || blocked || introRef.current.slide === slideId) return undefined;
    introRef.current.slide = slideId;
    let cancelled = false;
    api.get(`/voice/intro?slide=${slideId}`).then(({ data }) => {
      const el = audioRef.current;
      const gapMs = (data.gap_s ?? 90) * 1000;
      if (cancelled || !data.enabled || !data.url || !el || !el.paused || Date.now() - introRef.current.at < gapMs) return;
      introRef.current.at = Date.now();
      play(`${BASE}${data.url}`, data.url.split("/").pop().replace(".mp3", ""), "intro");
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId, blocked]);
  // Warm the next slide's intro clip so TTS latency never eats into its airtime
  useEffect(() => {
    if (!nextSlideId || SLIDE_VOICE[nextSlideId] || introRef.current.warmed === nextSlideId) return;
    introRef.current.warmed = nextSlideId;
    api.get(`/voice/intro?slide=${nextSlideId}`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextSlideId]);

  useEffect(() => { onSpeaking?.(state.speaking); }, [state.speaking, onSpeaking]);

  // Stop any in-progress announcer the moment a breaking takeover blocks the loop.
  useEffect(() => {
    if (!block) return;
    const el = audioRef.current;
    if (el && !el.paused) { el.pause(); setState((s) => ({ ...s, speaking: false })); }
  }, [block]);

  return (
    <audio ref={audioRef} data-testid="weather-voice" data-clip={state.clipId || ""} data-kind={state.kind || ""} data-speaking={state.speaking ? "1" : "0"} preload="auto"
      onEnded={() => setState((s) => ({ ...s, speaking: false }))} onError={() => setState((s) => ({ ...s, speaking: false }))} />
  );
}
